import { TooltipWrapper } from 'components/TooltipWrapper';
import { ActKc3LevelEnum, WorkOrderStatusEnum, ContractStatusEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { KebabActKC3 } from 'pages/Project/UerProject/ActKC3List/components/KebabActKC3/KebabActKC3';
import { Button, Checkbox, IconExpand, IconAdd } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';
import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import type { ActListItemWithGroupInfo } from '../types';

interface TMenuRenderer extends ICellRendererParams<ActListItemWithGroupInfo> {
    checkedRows: Record<number, boolean>;
    checkRowHandler: ({ id, checked }: { id: number; checked: boolean }) => void;
    openSingleDelete: (data: ActListItemWithGroupInfo) => void;
    setDataForTotals: (data: ActListItemWithGroupInfo) => void;
    refreshListActListData: () => void;
    onActKc3Add: (data: ActListItemWithGroupInfo) => void;
    actKC3Level: ActKc3LevelEnum;
}

export const MenuRenderer = (props: TMenuRenderer) => {
    const { id: idProject } = useParams();
    const {
        data,
        checkRowHandler,
        checkedRows,
        openSingleDelete,
        refreshListActListData,
        setDataForTotals,
        onActKc3Add,
        actKC3Level,
    } = props;

    const {
        actKC3CanDelete,
        actKC3Id,
        contractId,
        actKC3CanEdit,
        isGroup,
        actKC3IsCorrection,
        isFinal,
        actKC2Date,
        actKC3IsInactive,
        level,
        hasWorkOrdersInProgress,
    } = data ?? {};

    const { aktKC3: aktKC3Rules } = useAccess();
    const { projectData } = useProjectData(idProject, false);

    const { status: projectStatus, contractNavigation } = projectData ?? {};
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const isProjectAndContractStatusActive = useMemo(
        () =>
            contractNavigation?.data?.contractNavData?.status === ContractStatusEnum.InImplementation &&
            projectStatus === ProjectStatusEnum.InImplementation,
        [contractNavigation?.data?.contractNavData?.status, projectStatus],
    );

    const isAddDisabled = useMemo(() => {
        if (!aktKC3Rules?.canCreate) {
            return true;
        }

        if (actKC3Level === ActKc3LevelEnum.Object) {
            return !hasWorkOrdersInProgress;
        }

        return actKC3Level === ActKc3LevelEnum.WorkOrder && data?.groupStatus !== WorkOrderStatusEnum.InProgress;
    }, [actKC3Level, aktKC3Rules?.canCreate, data?.groupStatus, hasWorkOrdersInProgress]);

    const isEditable = useMemo(
        () => !isAddDisabled && ((actKC3CanEdit && aktKC3Rules?.canEdit && isProjectAndContractStatusActive) ?? false),
        [isAddDisabled, actKC3CanEdit, aktKC3Rules?.canEdit, isProjectAndContractStatusActive],
    );

    const isRemovable = useMemo(
        () =>
            !isAddDisabled &&
            ((actKC3CanDelete && aktKC3Rules?.canEdit && isProjectAndContractStatusActive && !actKC3IsInactive) ??
                false),
        [isAddDisabled, actKC3CanDelete, actKC3IsInactive, aktKC3Rules?.canEdit, isProjectAndContractStatusActive],
    );

    const isExportable = useMemo(() => aktKC3Rules?.canExportExcel, [aktKC3Rules?.canExportExcel]);

    const openSingleDeleteHandler = useCallback(() => {
        if (data) {
            openSingleDelete(data);
        }
    }, [data, openSingleDelete]);

    const onClickHandler = useCallback(
        ({ checked }) => {
            checkRowHandler({
                id: actKC3Id,
                checked,
            });
        },
        [actKC3Id, checkRowHandler],
    );

    const onRefreshList = useCallback(
        (needUpdate: boolean | undefined) => {
            if (needUpdate) {
                refreshListActListData();
            }
        },
        [refreshListActListData],
    );

    const openTotals = useCallback(() => {
        if (data) {
            setDataForTotals(data);
        }
    }, [data, setDataForTotals]);

    const onActKc3AddLocal = useCallback(() => {
        if (data) {
            onActKc3Add(data);

            sendGpnAnalyticsEvent({
                componentId: 'rim_add KS TRI button_pressed',
                projectId: idProject,
                contractId: projectData?.contractId,
                orderId: data?.workOrderId,
                methodology: projectData?.methodology,
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
            });
        }
    }, [
        data,
        idProject,
        onActKc3Add,
        projectData?.contractId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        sendGpnAnalyticsEvent,
    ]);

    return isGroup ? (
        <>
            {!!level && (
                <>
                    <Checkbox checked={checkedRows[actKC3Id]} disabled={!isRemovable} onChange={onClickHandler} />

                    <KebabActKC3
                        actKC3Id={actKC3Id}
                        isFinal={isFinal}
                        contractId={contractId}
                        isEditable={isEditable}
                        isRemovable={isRemovable}
                        isExportable={isExportable}
                        onClose={onRefreshList}
                        openSingleDelete={openSingleDeleteHandler}
                        isCorrection={Boolean(actKC3IsCorrection)}
                        data={data}
                        actKC3Level={actKC3Level}
                        isOnlyDateEditable={!!actKC3IsCorrection && !isFinal && !actKC2Date}
                    />

                    <Button onlyIcon iconLeft={IconExpand} size="xs" view="clear" onClick={openTotals} />
                </>
            )}
            {!level && (
                <div style={{ width: '100%' }}>
                    <TooltipWrapper
                        direction="upCenter"
                        message="Сформировать Справку КС-3"
                        style={{ marginRight: 'auto', width: 32 }}
                        isHidden={isAddDisabled}
                    >
                        <Button
                            iconLeft={IconAdd}
                            onClick={onActKc3AddLocal}
                            onlyIcon
                            iconSize="xs"
                            size="s"
                            view="secondary"
                            disabled={isAddDisabled}
                        />
                    </TooltipWrapper>
                </div>
            )}
        </>
    ) : (
        ''
    );
};

MenuRenderer.displayName = 'ActKC3ListMenuRenderer';
